import { useCallback, useRef, useState } from 'react';
import { endpoints } from '@/api';
import { ApiError, errorMessage } from '@/api/errors';
import type { AdsConfig, Me, RewardedPlacement } from '@/api/types';
import { useAuth } from '@/stores/auth';
import { getAdProvider } from './runtime';

/** SPEC_V5 §2 limits (server-enforced; mirrored for display only). */
export const FILTER_PASS_DAILY = 2;
export const FILTER_PASS_MINUTES = 30;
export const FRIEND_BONUS_DAILY = 1;
export const FRIEND_BONUS = 5;

export type RewardedOutcome =
  | { kind: 'granted'; me: Me }
  | { kind: 'closed' }
  | { kind: 'unavailable' }
  | { kind: 'capped' }
  | { kind: 'already_plus' }
  | { kind: 'too_fast' }
  | { kind: 'token_expired' }
  | { kind: 'token_used' }
  | { kind: 'drop_over' }
  | { kind: 'error'; message: string };

function mapError(e: unknown): RewardedOutcome {
  if (e instanceof ApiError) {
    const r = e.details.reason;
    if (e.status === 429) return r === 'reward_cap' ? { kind: 'capped' } : { kind: 'error', message: 'That’s a lot of ads for one hour. Try again a bit later.' };
    if (e.status === 409) {
      if (r === 'already_plus' || e.code === 'already_plus') return { kind: 'already_plus' };
      if (r === 'token_used') return { kind: 'token_used' };
      if (r === 'token_expired') return { kind: 'token_expired' };
      if (r === 'too_fast') return { kind: 'too_fast' };
      if (r === 'drop_over') return { kind: 'drop_over' };
    }
    if (e.status === 404) return { kind: 'unavailable' };
  }
  return { kind: 'error', message: errorMessage(e) };
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * The server token protocol (ARCHITECTURE §21): start → show → on granted
 * complete; closed early → `ad_rewarded_closed`. Never throws.
 */
export async function runRewardedFlow(ads: AdsConfig, placement: RewardedPlacement, dropId?: string): Promise<RewardedOutcome> {
  if (!ads.units.rewarded) return { kind: 'unavailable' };
  const provider = await getAdProvider(ads);
  if (!provider) return { kind: 'unavailable' };

  let token: string;
  try {
    token = (await endpoints.adsRewardedStart(placement, dropId)).token;
  } catch (e) {
    return mapError(e);
  }

  let shown: 'granted' | 'closed' | 'unavailable';
  try {
    shown = await provider.showRewarded(placement);
  } catch {
    shown = 'unavailable';
  }
  if (shown === 'unavailable') return { kind: 'unavailable' };
  if (shown === 'closed') {
    void endpoints.trackEvent({ name: 'ad_rewarded_closed', props: { placement } }).catch(() => undefined);
    return { kind: 'closed' };
  }

  // Granted: the ad played to the end. Verify with the server.
  for (let attempt = 0; ; attempt++) {
    try {
      return { kind: 'granted', me: await endpoints.adsRewardedComplete(token) };
    } catch (e) {
      const out = mapError(e);
      // A clock hair short of min_watch_s: the token stays usable, retry once quietly.
      const wait = e instanceof ApiError ? e.retryAfter : null;
      if (out.kind === 'too_fast' && attempt === 0 && wait !== null && wait <= 20) {
        await sleep(wait * 1000 + 250);
        continue;
      }
      return out;
    }
  }
}

export interface RewardedNotice {
  tone: 'success' | 'info' | 'error';
  text: string;
  kind: RewardedOutcome['kind'];
}

const GRANTED: Record<RewardedPlacement, string> = {
  filter_pass: `Filters are on for ${FILTER_PASS_MINUTES} minutes.`,
  friend_requests: `+${FRIEND_BONUS} friend requests added for today.`,
  drop_early_access: 'You moved up the waitlist.',
  coins: '+5 coins added to your wallet.',
};
const CAPPED: Record<RewardedPlacement, string> = {
  filter_pass: 'That’s today’s filter passes. Back tomorrow.',
  friend_requests: 'You’ve already had today’s bonus. Back tomorrow.',
  drop_early_access: 'You’ve already moved up for this Drop.',
  coins: 'That’s today’s coin ads. Back tomorrow.',
};

/** Calm, plain copy for every outcome (no alarms, no guilt). */
export function rewardedNotice(placement: RewardedPlacement, o: RewardedOutcome): RewardedNotice {
  switch (o.kind) {
    case 'granted':
      return { tone: 'success', text: GRANTED[placement], kind: o.kind };
    case 'closed':
      return { tone: 'info', text: 'The ad has to play to the end to count. No worries — try again any time.', kind: o.kind };
    case 'unavailable':
      return { tone: 'info', text: 'No ad to show right now. Try again in a little while.', kind: o.kind };
    case 'capped':
      return { tone: 'info', text: CAPPED[placement], kind: o.kind };
    case 'already_plus':
      return { tone: 'info', text: 'You already have Plus, so this is unlocked.', kind: o.kind };
    case 'too_fast':
      return { tone: 'info', text: 'That ad ended a little early, so it didn’t count. Give it another go.', kind: o.kind };
    case 'token_expired':
      return { tone: 'info', text: 'That took a while, so it timed out. Try again.', kind: o.kind };
    case 'token_used':
      return { tone: 'info', text: 'That ad was already counted.', kind: o.kind };
    case 'drop_over':
      return { tone: 'info', text: 'This Drop has ended.', kind: o.kind };
    default:
      return { tone: 'error', text: o.message, kind: o.kind };
  }
}

/** UI wrapper: runs the flow, applies the returned Me, keeps a calm inline notice. */
export function useRewardedAd(ads: AdsConfig | null, placement: RewardedPlacement, opts: { dropId?: string; onDone?: (o: RewardedOutcome) => void } = {}) {
  const setMe = useAuth((s) => s.setMe);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<RewardedNotice | null>(null);
  const running = useRef(false);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const run = useCallback(async (): Promise<RewardedOutcome> => {
    if (!ads) return { kind: 'unavailable' };
    if (running.current) return { kind: 'error', message: 'Already running' };
    running.current = true;
    setBusy(true);
    setNotice(null);
    try {
      const o = await runRewardedFlow(ads, placement, optsRef.current.dropId);
      if (o.kind === 'granted') setMe(o.me);
      if (o.kind === 'already_plus' || o.kind === 'token_used' || o.kind === 'capped') {
        void endpoints
          .me()
          .then(setMe)
          .catch(() => undefined);
      }
      setNotice(rewardedNotice(placement, o));
      optsRef.current.onDone?.(o);
      return o;
    } finally {
      running.current = false;
      setBusy(false);
    }
  }, [ads, placement, setMe]);

  return { run, busy, notice, clearNotice: () => setNotice(null) };
}
